import { tv } from "tailwind-variants";

// Font scaling calculation:
// The `clamp(min, preferred, max)` function ensures font sizes scale fluidly within a given range.
// To calculate `preferred` (vw-based scaling), use:
//
// 1. Calculate the scale factor (how much the font grows):
//    scale = (max - min) / (max-width - min-width)
//
// 2. Convert to vw (viewport width percentage):
//    preferred-scale = scale * 100vw
//
// 3. Calculate the base size (px value to add/subtract):
//    preferred-base = (min-width * max - max-width * min) / (min-width - max-width)
//
// 4. Final formula:
//    clamp(min, preferred-scale + preferred-base, max)
//
// Example:
// For `clamp(60px, ?, 80px)` at 2xl (1536px → 1920px):
//    scale = (80 - 60) / (1920 - 1536) = 20 / 384 = 0.0521
//    preferred-scale = 5.21vw
//    preferred-base = (1536 * 80 - 1920 * 60) / (1536 - 1920) = -20px
//    Final: `clamp(60px, 5.21vw - 20px, 80px)`
//    Tailwind Class: `text-[clamp(60px,5.21vw-20px,80px)]`

// Tailwind breakpoints used:
// (min screen 375px), screen-md = md (768px), screen-lg = xl (1280px),
// screen-xl = 2xl (1536px), screen-2xl = 3xl (1920px)

export const textVariants = tv({
  base: "font-sans leading-none text-current",
  variants: {
    size: {
      h1: "text-[2.75rem] font-semibold leading-[100%] tracking-[-2.2px] lg:text-[6.25rem] lg:tracking-[-5px]",
      h2: "text-[2.5rem] font-semibold leading-[100%] tracking-[-2px] lg:text-[5rem] lg:tracking-[-4px]",
      h3: "text-[2.25rem] font-semibold leading-[100%] tracking-[-1.8px] lg:text-[4rem] lg:tracking-[-2.5px]",
      h4: "text-[1.5rem] font-semibold leading-[115%] tracking-[-0.96px] lg:text-[2rem] lg:tracking-[-1.28px]",
      h5: "text-[1.125rem] font-semibold leading-[115%] tracking-[-0.36px]",
      body: "text-[1rem] font-normal leading-[135%]",
      quote:
        "font-serif text-[1.5rem] leading-[115%] tracking-[-0.96px] lg:text-[1.75rem] lg:tracking-[-1.12px]",
      subtitle: "text-[0.75rem] uppercase leading-[130%] tracking-[1.2px]",
    },
    anchor: {
      true: "relative cursor-pointer bg-gradient-to-r from-current to-current bg-[size:100%_0.05rem] bg-[position:0%_100%] bg-no-repeat",
    },
    animateIn: { true: "", false: "" },
    animateOut: { true: "", false: "" },
    caret: { true: "", false: "" },
  },
  defaultVariants: {
    anchor: false,
    animateIn: false,
    animateOut: false,
    caret: false,
    size: "body",
  },
  compoundVariants: [
    {
      anchor: true,
      animateIn: true,
      className:
        "bg-[size:0%_0.05rem] bg-[position:100%_100%] [transition:background-size_0.2s_ease-out] hover:bg-[size:100%_0.05rem] hover:bg-[position:0%_100%]",
    },
    {
      anchor: true,
      animateOut: true,
      className:
        "bg-[size:100%_0.05rem] bg-[position:0%_100%] [transition:background-size_0.2s_ease-out] hover:bg-[size:0%_0.05rem] hover:bg-[position:100%_100%]",
    },
    {
      anchor: true,
      caret: true,
      className:
        "relative mr-[0.78em] after:absolute after:right-[-1.15rem] after:top-1/2 after:block after:-translate-y-[45%] after:text-[0.7rem] after:leading-[1.1] after:content-['▶']",
    },
  ],
});
